import { useState } from "react";
import {
  Select,
  Tooltip,
  Modal,
  Typography,
  Form,
  InputNumber,
} from "antd";
import {
  UserOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  useCompanySuppliers,
  type CompanySupplier,
} from "../../../hooks/useCompanySuppliers";
import { useCompany } from "../../../hooks/useCompany";

export interface SupplierFormValues {
  supplier_name: string;
  supplier_email: string;
  supplier_phone: string;
  supplier_wechat?: string;
  supplier_country: string;
  supplier_province: string;
  supplier_city: string;
  supplier_address: string;
}

interface SavedSupplierPickerProps {
  onSelect: (values: SupplierFormValues, supplier: CompanySupplier | null) => void;
}

export const SavedSupplierPicker = ({ onSelect }: SavedSupplierPickerProps) => {
  const { t } = useTranslation();
  const { selectedCompany } = useCompany();
  const {
    suppliers: companySuppliers,
    deleteCompanySupplier,
  } = useCompanySuppliers(selectedCompany);

  const [selectedId, setSelectedId] = useState<number | undefined>();
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [supplierToDelete, setSupplierToDelete] =
    useState<CompanySupplier | null>(null);

  const applySupplier = (supplier: CompanySupplier | null) => {
    if (!supplier) {
      onSelect(
        {
          supplier_name: "",
          supplier_email: "",
          supplier_phone: "",
          supplier_wechat: undefined,
          supplier_country: "",
          supplier_province: "",
          supplier_city: "",
          supplier_address: "",
        },
        null
      );
      return;
    }

    onSelect(
      {
        supplier_name: supplier.name,
        supplier_email: supplier.email,
        supplier_phone: supplier.phone,
        supplier_wechat: supplier.wechat ?? undefined,
        supplier_country: supplier.country,
        supplier_province: supplier.province,
        supplier_city: supplier.city,
        supplier_address: supplier.address,
      },
      supplier
    );
  };

  const handleSelect = (value: number | undefined) => {
    setSelectedId(value);
    if (!value) {
      applySupplier(null);
      return;
    }
    const supplier = companySuppliers.find((s) => s.id === value);
    if (supplier) applySupplier(supplier);
  };

  const handleDeleteClick = (
    supplier: CompanySupplier,
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation();
    setSupplierToDelete(supplier);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await deleteCompanySupplier(supplierToDelete.id);
      if (selectedId === supplierToDelete.id) {
        setSelectedId(undefined);
        applySupplier(null);
      }
    } finally {
      setDeleteModalVisible(false);
      setSupplierToDelete(null);
    }
  };

  return (
    <>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        {t("bookingWizard.supplierDetails.selectSupplier")}
      </Typography.Text>
      <Select
        style={{ width: "100%", marginBottom: 16 }}
        placeholder={t("bookingWizard.supplierDetails.selectSupplier")}
        onChange={handleSelect}
        value={selectedId}
        allowClear
        optionLabelProp="label"
        notFoundContent={t("bookingWizard.supplierDetails.noSavedSuppliers")}
      >
        {companySuppliers.map((supplier) => (
          <Select.Option key={supplier.id} value={supplier.id} label={supplier.name}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <UserOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                <div>
                  <div>{supplier.name}</div>
                  <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {supplier.email} • {supplier.country}, {supplier.city}
                  </div>
                </div>
              </div>
              <Tooltip title={t("bookingWizard.supplierDetails.deleteSupplier")}>
                <DeleteOutlined
                  style={{ color: "#ff4d4f" }}
                  onClick={(e) => handleDeleteClick(supplier, e)}
                />
              </Tooltip>
            </div>
          </Select.Option>
        ))}
      </Select>

      <Modal
        title={t("bookingWizard.supplierDetails.deleteSupplier")}
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSupplierToDelete(null);
        }}
        okText={t("common.delete")}
        okButtonProps={{ danger: true }}
        cancelText={t("common.cancel")}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <ExclamationCircleOutlined
            style={{ color: "#faad14", fontSize: 22, marginRight: 16 }}
          />
          <Typography.Text>
            {t("bookingWizard.supplierDetails.confirmDeleteSupplier")}
          </Typography.Text>
        </div>
        <Typography.Text type="secondary">{supplierToDelete?.name}</Typography.Text>
      </Modal>
    </>
  );
};
